import { useTranslation } from 'react-i18next';
import { Area, CartesianGrid, ComposedChart, Line, XAxis, YAxis } from 'recharts';

import { ChartConfig, ChartContainer, ChartLegend, ChartLegendContent, ChartTooltip, ChartTooltipContent } from '@/components/ui/chart';
import { SectionCard, formatCompact, formatDate } from '../../../components/design-system/admin';
import type { OverviewSeriesPoint } from '../../../common/models/admin-overview.model';

/** Visualizations (ink, filled) and visits (grey line) per day, all vendors. Monochrome by design. */
export function ActivityChart({ series, className }: { series: OverviewSeriesPoint[]; className?: string }) {
  const { t } = useTranslation();
  const config: ChartConfig = {
    visualizations: { label: t('ADMIN_OVERVIEW.ACTIVITY.VISUALIZATIONS'), color: 'var(--foreground)' },
    visits: { label: t('ADMIN_OVERVIEW.ACTIVITY.VISITS'), color: 'var(--muted-foreground)' },
  };
  const short = (d: string) => new Intl.DateTimeFormat(undefined, { day: 'numeric', month: 'short' }).format(new Date(`${d}T00:00:00Z`));

  return (
    <SectionCard title={t('ADMIN_OVERVIEW.ACTIVITY.TITLE')} description={t('ADMIN_OVERVIEW.ACTIVITY.DESCRIPTION')} className={className} testId="overview-activity">
      <ChartContainer config={config} className="aspect-auto h-64 w-full">
        <ComposedChart data={series} margin={{ left: -12, right: 4, top: 8, bottom: 0 }}>
          <defs>
            <linearGradient id="overview-viz" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="var(--color-visualizations)" stopOpacity={0.14} />
              <stop offset="100%" stopColor="var(--color-visualizations)" stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid vertical={false} strokeDasharray="3 3" />
          <XAxis dataKey="date" tickLine={false} axisLine={false} tickMargin={8} minTickGap={24} tickFormatter={short} />
          <YAxis tickLine={false} axisLine={false} width={44} tickFormatter={(v) => formatCompact(v)} />
          <ChartTooltip content={<ChartTooltipContent labelFormatter={(d) => formatDate(`${d}T00:00:00Z`)} />} />
          <ChartLegend content={<ChartLegendContent />} />
          <Area type="monotone" dataKey="visualizations" stroke="var(--color-visualizations)" strokeWidth={1.75} fill="url(#overview-viz)" isAnimationActive={false} />
          <Line type="monotone" dataKey="visits" stroke="var(--color-visits)" strokeWidth={1.5} dot={false} isAnimationActive={false} />
        </ComposedChart>
      </ChartContainer>
    </SectionCard>
  );
}
